import { Box, Stack, useTheme } from "@mui/material";
import { Typography } from "@foodpilot/foods";
import type { Dispatch, SetStateAction } from "react";
import { Controller, type Control } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { AssociatedOrganizationsField, SectionCard, SectionContent, SectionHeader } from "src/components/EntityForm";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

import type { IngredientFormType } from "../../IngredientForm.types";
import type { IngredientFormData } from "../../IngredientForm.schema";
import { FilliereField } from "../../Pages/Page1/FilliereField";
import { IngredientJsonAttributesField } from "../../Pages/Page1/Fields/IngredientJsonAttributesField";
import { IngredientTagField } from "../../Pages/Page1/Fields/IngredientTagField";

type IngredientOrganizationSectionProps = {
    control: Control<IngredientFormData>;
    ingredient: IngredientFormType;
    progress: { current: number; total: number };
    setIngredient: Dispatch<SetStateAction<IngredientFormType>>;
};

export const IngredientOrganizationSection = (props: IngredientOrganizationSectionProps) => {
    const { control, ingredient, progress, setIngredient } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { selectedCompany } = useFoodpilotContext();
    const owningCompany = ingredient.ownedBy;
    const canEditAssociations = owningCompany !== null && owningCompany.id === selectedCompany?.id;
    const displayedOrganizations = ingredient.shares.map(({ organization }) => organization);

    return (
        <SectionCard>
            <SectionHeader
                progress={progress}
                title={t("entity.organization.section_title", "Organisation")}
                subtitle={t(
                    "entity.organization.section_subtitle",
                    "Define the ingredient's positioning within your organization and the entities that will be able to contribute to it.",
                )}
            />
            <SectionContent sx={{ paddingBottom: theme.spacing(2) }}>
                <Controller
                    name="ingredientLine"
                    control={control}
                    render={({ field }) => (
                        <FilliereField
                            appearance="product-v2"
                            ingredient={ingredient}
                            onChange={setIngredient}
                            field={field}
                        />
                    )}
                />

                {canEditAssociations && (
                    <AssociatedOrganizationsField
                        organizations={displayedOrganizations}
                        excludedOrganizationIds={owningCompany === null ? [] : [owningCompany.id]}
                        hierarchyRootId={selectedCompany?.id}
                        label={t("entity.organization.associated_entities.label", "Associated entities:")}
                        helperText={t(
                            "entity.organization.associated_entities.helper",
                            "Select the companies that use this ingredient. They will be able to complete the data related to their suppliers and, depending on their access rights, manage the associated suppliers.",
                        )}
                        placeholder={t("entity.organization.associated_entities.placeholder", "Add an option")}
                        removeLabel={(organization) => `${t("common.actions.delete", "Delete")} ${organization.name}`}
                        onAdd={(organizations) =>
                            setIngredient((current) => ({
                                ...current,
                                shares: [...current.shares, ...organizations.map((organization) => ({ organization }))],
                            }))
                        }
                        onRemove={(organizationId) =>
                            setIngredient((current) => ({
                                ...current,
                                shares: current.shares.filter(({ organization }) => organization.id !== organizationId),
                            }))
                        }
                    />
                )}

                <Box
                    role="separator"
                    aria-orientation="horizontal"
                    sx={{
                        borderBottomColor: theme.custom.strokes.containerDivider.color,
                        borderBottomStyle: theme.custom.strokes.containerDivider.style,
                        borderBottomWidth: theme.custom.strokes.containerDivider.width,
                    }}
                />

                <Stack sx={{ gap: theme.spacing(1) }}>
                    <Typography variant="body-bold">{t("entity.tags.title", "Tags")}</Typography>
                    <Controller
                        name="tags"
                        control={control}
                        render={({ field }) => (
                            <IngredientTagField
                                appearance="product-v2"
                                ingredient={ingredient}
                                setIngredient={setIngredient}
                                field={field}
                            />
                        )}
                    />
                </Stack>

                <IngredientJsonAttributesField
                    appearance="product-v2"
                    ingredient={ingredient}
                    setIngredient={setIngredient}
                />
            </SectionContent>
        </SectionCard>
    );
};
